import styled, { keyframes } from "styled-components";

import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
  ANIMATION,
} from "../../../utils/constants";

// Shimmer sweep for skeleton placeholders. The global prefers-reduced-motion
// rule in GlobalStyles stills this for motion-sensitive users.
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

export const Wrapper = styled.div`
  display: flex;
  background: var(--color-background-0);
  flex-direction: column;
  gap: 0;
  padding: 0;
`;

// Generic section wrapper applying the page's responsive horizontal margin
export const SectionWrapper = styled.div<{ $isFirst?: boolean }>`
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;
  margin-top: ${(props) => (props.$isFirst ? "" : "0px")};

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
    margin-top: ${(props) => (props.$isFirst ? "" : "0px")};
  }

  @media ${QUERIES.laptopAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
    margin-top: ${(props) => (props.$isFirst ? "" : "0px")};
  }
`;

// Theme-aware skeleton primitive (adapts to light/dark via the gradient token).
export const SkeletonBox = styled.div<{ width?: string; height?: string }>`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "20px"};
  background: var(--gradient-skeleton);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ${ANIMATION.easing.easeInOut} infinite;
  border-radius: 4px;
`;

// Mirrors LiabilityHero's HeroLayout: stacked cards on mobile, a single divided
// banner (summary 1.6fr / next payment 1fr) from laptop up.
export const HeroSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;

  @media ${QUERIES.laptopAndUp} {
    display: grid;
    grid-template-columns: 1.6fr 1fr;
    gap: 0;
    align-items: stretch;
    background: var(--hero-background);
    border: 1px solid var(--color-card-border);
    border-radius: 16px;
    box-shadow: var(--shadow-small);
    overflow: hidden;
  }
`;

export const HeroPanel = styled.div<{ $divided?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 16px;
  box-shadow: var(--shadow-small);

  @media ${QUERIES.laptopAndUp} {
    gap: ${SPACING.md}px;
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    border-inline-start: ${({ $divided }) =>
      $divided ? "1px solid var(--color-border)" : "0"};
  }
`;

export const HeroActionsRow = styled.div`
  display: flex;
  gap: ${SPACING.sm}px;
  justify-content: flex-end;
`;

// Mirrors the padded section cards (BreakdownCard, TrendsCard): radius 16,
// padding md → lg, no resting shadow escalation.
export const CardSkeleton = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px;
  min-width: 0;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 16px;
  box-shadow: var(--shadow-small);

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING.lg}px;
  }
`;

export const RowBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
`;

export const StatGridSkeleton = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${SPACING.md}px;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: repeat(3, 1fr);
    gap: ${SPACING.lg}px;
  }
`;

export const StatCellSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const DetailsGridSkeleton = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${SPACING.md}px;
  padding-top: ${SPACING.md}px;
  border-top: 1px solid var(--color-border);

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

// Mirrors LiabilityTrends' header row (title block + range toggle) and chart.
export const TrendsHeaderSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;

  @media ${QUERIES.tabletAndUp} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

export const TitleBlockSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const ChartAreaSkeleton = styled(SkeletonBox)`
  height: 280px;

  @media ${QUERIES.tabletLargeAndUp} {
    height: 340px;
  }
`;

// Mirrors the flush table cards (InterestRateHistory, PaymentHistory): radius
// 16, overflow hidden, rows divided by hairline borders.
export const TableCardSkeleton = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 16px;
  box-shadow: var(--shadow-small);
  overflow: hidden;
`;

export const TableHeaderSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px;

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING.lg}px;
  }
`;

export const TableRowSkeleton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px;
  border-top: 1px solid var(--color-border);
`;
